import { useQuery } from '@apollo/client'
import { Box, CircularProgress, Divider, Stack } from '@mui/material'
import { type GridRowParams } from '@mui/x-data-grid-pro'
import { gql } from '__generated__'
import StarterKit from '@tiptap/starter-kit'
import { RichTextReadOnly } from 'mui-tiptap'
import { type FC } from 'react'
import RosterCandidateRecruiterNotes from 'recruiter/pages/Rosters/RosterProfile/CandidateRosterNoteDrawer/RosterCandidateRecruiterNotes'
import { getPxAsRem } from 'shared/utils'
import { type RosterTableRowModel } from 'recruiter/components/rosterTableColumns'
import RosterPanelJobBoard from './RosterPanelJobBoard'
import RosterPanelProcessStatus from './RosterPanelProcessStatus'

const GetRosterCandidatePanelData = gql(/* GraphQL */ `
    query GetRosterCandidatePanelData($rosterCandidateId: ID!) {
        rosterCandidate(rosterCandidateId: $rosterCandidateId) {
            id
            roster {
                id
                pitchNote {
                    note
                }
                permissions {
                    canEdit
                }
            }
            candidateProcessStatusHistory {
                processStatus {
                    id
                    status
                    statusLabel
                    processStatusSummaryGroup {
                        id
                        summaryGroup
                        label
                    }
                }
                isActive
                createdAt
                updatedAt
                createdByUser {
                    firstName
                    lastName
                    nickname
                }
            }
            mostRecentJobBoardResponse {
                updatedAt
                createdAt
                updatedByUser {
                    firstName
                    lastName
                    nickname
                }
                createdByUser {
                    firstName
                    lastName
                    nickname
                }
                id
                isInterested
                notInterestedReason
                comments
                officePrefs {
                    companyOfficeId
                    companyOffice {
                        id
                        officeLocation {
                            value
                        }
                    }
                }
                sectorPrefs {
                    sector {
                        id
                        value
                    }
                }
                jobBoard {
                    titles {
                        title {
                            value
                            id
                        }
                    }
                    sectors {
                        sector {
                            id
                            value
                        }
                    }
                    company {
                        value
                    }
                    startDate
                    companyOffices {
                        companyOffice {
                            id
                            officeLocation {
                                value
                                id
                            }
                        }
                    }
                    name
                    description
                }
            }
        }
    }
`)

interface RosterSqlPanelContentProps {
    data: GridRowParams<RosterTableRowModel>
}

const RosterSqlPanelContent: FC<RosterSqlPanelContentProps> = ({ data }) => {
    const rosterCandidateId = data.row.rosterCandidateId
    const rosterId = data.row.id

    const { data: panelData, loading } = useQuery(GetRosterCandidatePanelData, {
        variables: { rosterCandidateId: rosterCandidateId ?? '' },
        skip: !rosterCandidateId,
    })

    if (!rosterCandidateId) {
        return (
            <Stack sx={{ m: getPxAsRem(16, 32), py: 2 }}>
                <Box color="text.secondary">
                    No roster candidate data available
                </Box>
            </Stack>
        )
    }

    if (loading) {
        return (
            <Stack
                sx={{ m: getPxAsRem(16, 32), py: 4 }}
                alignItems="center"
                justifyContent="center"
            >
                <CircularProgress size={24} />
            </Stack>
        )
    }

    const rosterCandidate = panelData?.rosterCandidate
    const canEdit = rosterCandidate?.roster?.permissions?.canEdit ?? false

    return (
        <Stack sx={{ m: getPxAsRem(16, 32) }} spacing={2}>
            <Box color="GrayText">
                <RichTextReadOnly
                    extensions={[StarterKit]}
                    content={rosterCandidate?.roster?.pitchNote?.note ?? '-'}
                />
            </Box>
            <Divider />
            <Stack direction="row" flexWrap="wrap" gap={2}>
                <RosterCandidateRecruiterNotes
                    rosterCandidateId={rosterCandidateId}
                />
                <Box sx={{ flex: '0 1 auto', minWidth: 200 }}>
                    <RosterPanelProcessStatus
                        rosterId={rosterId}
                        history={rosterCandidate?.candidateProcessStatusHistory}
                        rosterCandidateId={rosterCandidateId}
                        canEdit={canEdit}
                    />
                </Box>
            </Stack>

            {rosterCandidate?.mostRecentJobBoardResponse && (
                <RosterPanelJobBoard
                    canEdit={canEdit}
                    jobBoardData={rosterCandidate.mostRecentJobBoardResponse}
                />
            )}
        </Stack>
    )
}

export default RosterSqlPanelContent
